import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, isTokenExpired, requestAccessToken } from '../util/token';
import router from "../routes/router";
import { useMessageStore } from "../stores/messageStore";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean;
}

type QueueItem = {
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
};

const showErrorToast = (content: string) => useMessageStore.setState({
	isShow: true,
	type: 'error',
	content,
});

const handleLogout = async (message: string) => {
	localStorage.clear();
	showErrorToast(message);
	await router.navigate('/login');
};

const apiClient = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token?: string) => {
	failedQueue.forEach((prom) => {
		if (error) prom.reject(error);
		else if (token) prom.resolve(token);
	});
	failedQueue = [];
};

const handleTokenRefresh = (): Promise<string> => {
	const refreshToken = getRefreshToken();

	if (!refreshToken || isTokenExpired(refreshToken)) {
		return Promise.reject(new Error('REFRESH_TOKEN_EXPIRED'));
	}

	return requestAccessToken(refreshToken);
};



// REQUEST
apiClient.interceptors.request.use(
	async (config) => {
		const accessToken = getAccessToken();
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);



// RESPONSE
apiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError<any>) => {
		const originalRequest = error.config as CustomAxiosRequestConfig;

		if (!error.response) return Promise.reject(error);

		const status = error.response.status;
		const code = error.response.data?.code;
		const message = error.response.data?.message;

		if ((status === 401 || status === 500) && code === 'EXPIRED_TOKEN' && !originalRequest._retry) {
			if (originalRequest.url?.includes('/api/auth/token/refresh')) {
				await handleLogout('로그인 세션이 만료되었습니다.');
				return Promise.reject(error);
			}

			if (isRefreshing) {
				try {
					const token = await new Promise<string>((resolve, reject) => {
						failedQueue.push({ resolve, reject });
					});
					if (originalRequest.headers) {
						originalRequest.headers.set('Authorization', `Bearer ${token}`);
					}
					return apiClient(originalRequest);
				} catch (err) {
					return Promise.reject(err);
				}
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				const newAccessToken = await handleTokenRefresh();

				isRefreshing = false;
				processQueue(undefined, newAccessToken);

				if (originalRequest.headers) {
					originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);
				}
				return apiClient(originalRequest);

			} catch (refreshError) {
				isRefreshing = false;
				processQueue(refreshError);
				await handleLogout('로그인 세션이 만료되었습니다. 다시 로그인해 주세요.');
				return Promise.reject(refreshError);
			}
		}

		showErrorToast(error.response.data?.message || '요청 중 오류가 발생했습니다.');
		return Promise.reject(error);
	}
);

export default apiClient;